import React, { useCallback, useMemo, useRef, useState } from "react"
import { ConfigProvider } from "antd"
import { useProjectStore } from "stores/projectStore"
import { Loader } from "shared/ui/Loader/Loader.component"
import EmptyState from "shared/ui/EmptyState/EmptyState.component"
import { notify } from "shared/ui/Notifications/notify"
import {
  AssistantDock,
  AssistantToggle,
  ProposalOf,
  ProposalStatus,
  selectConversation,
  useAssistantStore,
  useRegisterAssistantScreen,
} from "features/Assistant"
import { useSavedQueryNamesQuery } from "features/Provenance/api/useLineage"
import {
  LoadedSpec,
  useTabSpecDocument,
  validateSpecName,
} from "../api/useTabSpecFiles"
import RemovedTablesReview from "../components/Assistant/RemovedTablesReview.component"
import TabSpecWorkspace from "../components/TabSpecWorkspace.component"
import { TAB_SPEC_THEME } from "../components/tabSpecTheme"
import { contentHash } from "../model/hash"
import {
  createTabSpecStore,
  TabSpecStoreProvider,
  useTabSpecStore,
  useTabSpecStoreApi,
} from "../runtime/tabSpecStore"
import {
  droppedOnRead,
  normalizeTabSpecDocument,
  TableSpec,
  TabSpecDocument,
} from "../types/tabSpecDocument"
// The --qc-* tokens the tab spec shares live in the console stylesheet.
import "styles/queryConsole.css"
import "styles/tabSpec.css"

const REASON_TEXT: Record<"newer-version" | "invalid", string> = {
  "newer-version":
    "This tab spec was saved by a newer version of DATA(Q) and cannot be opened here.",
  invalid: "This tab spec could not be read.",
}

/** Why a loaded spec cannot be opened; no file (null) or no data is "not found". */
const reasonText = (data: LoadedSpec | null | undefined): string =>
  data && !data.result.ok ? REASON_TEXT[data.result.reason] : "Tab spec not found."

/** The toast when a proposal's document cannot be read; nothing is loaded. */
export const INVALID_PROPOSAL_TEXT =
  "The assistant's tables could not be read, so nothing was loaded."
/** The toast when reading the proposal drops banners, banner groups or tables; nothing is loaded. */
export const UNREADABLE_PARTS_TEXT =
  "Some of the assistant's banner groups or tables could not be read, so nothing was loaded. Ask again."
/** Why Review is off for a proposal made for another spec. */
export const otherSpecText = (name: string): string =>
  `This proposal is for ${name}; open it to review`
/** Why Review is off while the script the proposal uses is not saved. */
export const saveScriptText = (name: string): string => `Save the script ${name} first`

/**
 * `baseHash` of a document: `contentHash`, the hash of its canonical JSON
 * without the approval and the updated stamps. Worked out once per document
 * object (the store replaces the document on every edit), since the
 * proposal cards ask on every render.
 */
const hashes = new WeakMap<TabSpecDocument, string>()
export const specHash = (document: TabSpecDocument): string => {
  const known = hashes.get(document)
  if (known !== undefined) return known
  const hash = contentHash(document)
  hashes.set(document, hash)
  return hash
}

/** A proposal loaded into the draft, with the draft before it for its Undo. */
interface Loaded {
  before: TabSpecDocument
  beforeAiTableIds: string[]
  /**
   * The draft right after the load and the saved document then: Undo is
   * offered while both are still these (nothing edited, nothing saved).
   */
  after: TabSpecDocument
  saved: TabSpecDocument
}

/** A proposal that drops tables, waiting for the user to see which. */
interface Confirming {
  proposal: ProposalOf<"tabspec">
  document: TabSpecDocument
  removed: TableSpec[]
}

/**
 * Tells the assistant this is the Tables screen: the open spec goes with
 * each turn (`baseHash` says which version was asked about), and a Tables
 * proposal is reviewed here. Review loads the proposed document into the
 * draft, the tables it added or changed tagged "AI"; the spec is then
 * dirty, and the user saves it as any edit, or takes it back with the
 * card's Undo while nothing else has changed. A proposal that drops tables
 * says which first. Review is off for a proposal made for another spec, or
 * one that uses a script not saved yet; one asked about another version of
 * the spec is out of date.
 */
const TablesAssistant: React.FC = () => {
  const store = useTabSpecStoreApi()
  const projectId = useTabSpecStore((s) => s.projectId)
  const name = useTabSpecStore((s) => s.name)
  const document = useTabSpecStore((s) => s.document)
  const savedDocument = useTabSpecStore((s) => s.savedDocument)
  // The saved queries are read only while a proposal waits on a script.
  const waitsOnScript = useAssistantStore((s) =>
    selectConversation(projectId)(s).proposals.some(
      ({ proposal, status }) =>
        status === "pending" && proposal.kind === "tabspec" && !!proposal.requiresScript
    )
  )
  const scripts = useSavedQueryNamesQuery(projectId, waitsOnScript)
  const loaded = useRef(new Map<string, Loaded>())
  const [confirming, setConfirming] = useState<Confirming | null>(null)
  // The cards ask the handlers again when the draft, the saved spec or the
  // saved queries change: a new object then, the same one otherwise.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const revision = useMemo(() => ({}), [document, savedDocument, scripts.data])

  const setStatus = (id: string, status: ProposalStatus): void =>
    useAssistantStore.getState().setProposalStatus(projectId, id, status)

  const isStale = (p: ProposalOf<"tabspec">): boolean =>
    p.name === name && p.baseHash !== specHash(store.getState().document)

  const load = (p: ProposalOf<"tabspec">, proposed: TabSpecDocument): void => {
    const { document: before, aiTableIds: beforeAiTableIds, loadDraft } = store.getState()
    loadDraft(proposed, Array.isArray(p.changedTableIds) ? p.changedTableIds : [])
    const after = store.getState()
    loaded.current.set(p.id, {
      before,
      beforeAiTableIds,
      after: after.document,
      saved: after.savedDocument,
    })
    setStatus(p.id, "accepted")
  }

  const review = (p: ProposalOf<"tabspec">): void => {
    if (p.name !== name) return
    const result = normalizeTabSpecDocument(p.document)
    if (!result.ok) {
      notify.toast.error(INVALID_PROPOSAL_TEXT)
      return
    }
    // A banner, group or table reading drops would be missing from the draft:
    // loading the rest would show "Accepted" with the asked-for change gone.
    if (droppedOnRead(p.document, result.document).length > 0) {
      notify.toast.error(UNREADABLE_PARTS_TEXT)
      return
    }
    if (isStale(p)) {
      setStatus(p.id, "stale")
      return
    }
    // The spec's identity, approval and stamps are not the assistant's to change.
    const current = store.getState().document
    const proposed: TabSpecDocument = {
      ...result.document,
      id: current.id,
      name: current.name,
      approval: current.approval,
      updatedAt: current.updatedAt,
      updatedBy: current.updatedBy,
    }
    const kept = new Set(proposed.tables.map((t) => t.id))
    const removed = current.tables.filter((t) => !kept.has(t.id))
    if (removed.length > 0) setConfirming({ proposal: p, document: proposed, removed })
    else load(p, proposed)
  }

  const confirmLoad = (): void => {
    if (!confirming) return
    setConfirming(null)
    if (isStale(confirming.proposal)) setStatus(confirming.proposal.id, "stale")
    else load(confirming.proposal, confirming.document)
  }

  const undoable = (p: ProposalOf<"tabspec">): Loaded | null => {
    const was = loaded.current.get(p.id)
    const now = store.getState()
    return was && now.document === was.after && now.savedDocument === was.saved ? was : null
  }

  useRegisterAssistantScreen({
    screen: "tables",
    getContext: () => {
      const current = store.getState().document
      return { tabSpec: { name, document: current, baseHash: specHash(current) } }
    },
    revision,
    handlers: {
      tabspec: {
        review: (p) => {
          if (p.kind === "tabspec") review(p)
        },
        // Another spec's proposal is not out of date here: it is blocked.
        isStale: (p) => p.kind === "tabspec" && isStale(p),
        blockedReason: (p) => {
          if (p.kind !== "tabspec") return null
          if (p.name !== name) return otherSpecText(p.name)
          const script = p.requiresScript
          if (script && !(scripts.data ?? []).includes(script)) return saveScriptText(script)
          return null
        },
        canUndo: (p) => p.kind === "tabspec" && undoable(p) !== null,
        undo: (p) => {
          const was = p.kind === "tabspec" ? undoable(p) : null
          if (!was) return
          store.getState().revertTo(was.before, was.beforeAiTableIds)
          loaded.current.delete(p.id)
          // Back as before Review: it can be reviewed or rejected again.
          setStatus(p.id, "pending")
        },
      },
    },
  })

  return (
    <RemovedTablesReview
      removed={confirming?.removed ?? null}
      labels={document.labels}
      onLoad={confirmLoad}
      onCancel={() => setConfirming(null)}
    />
  )
}

/**
 * One loaded tab spec. The store is created once per mount (the route keys
 * the container by project and name): a save refreshes the query cache, but
 * the draft lives on in the store. `onReload` mounts it again from the
 * stored version. `dataOnly`: the project has no questionnaire. The
 * assistant's toggle sits at the right end of the spec's header.
 */
const TabSpecRoot: React.FC<{
  projectId: string
  owner: string
  name: string
  document: TabSpecDocument
  etag: string
  dataOnly: boolean
  onReload: () => Promise<void>
}> = ({ projectId, owner, name, document, etag, dataOnly, onReload }) => {
  const [store] = useState(() =>
    createTabSpecStore({ projectId, owner, name, document, etag, dataOnly })
  )
  return (
    <TabSpecStoreProvider store={store}>
      {/* The theme travels by React context, so it reaches every drawer,
          dialog and popup the spec opens, wherever antd renders them. */}
      <ConfigProvider theme={TAB_SPEC_THEME}>
        <div className="tabspec-root dq-workspace">
          {/* Inside the spec, so its dialog gets the --qc-* tokens. */}
          <TablesAssistant />
          <TabSpecWorkspace onReload={onReload} headerActions={<AssistantToggle />} />
        </div>
      </ConfigProvider>
    </TabSpecStoreProvider>
  )
}

/**
 * Loads the tab spec file `name` of the current project and opens it. The
 * name comes from the address: one the name dialog would refuse is "not
 * found", and nothing is sent for it. `dataOnly`: the project has no
 * questionnaire (the route knows from the project type); its tables come
 * from the data alone.
 */
const TabSpecContainer: React.FC<{ name: string; dataOnly?: boolean }> = ({
  name,
  dataOnly = false,
}) => {
  const project = useProjectStore((s) => s.project)
  const usable = validateSpecName(name) === null
  const loaded = useTabSpecDocument(project.id, project.Created_by, name, usable)
  const data = loaded.data
  // The store is made once per mount, so opening the stored version in place
  // of the draft ("Reload their version") mounts the spec again: the counter
  // is in its key. It moves on only when the fetch succeeds with a readable
  // document: a failed fetch keeps the old data, and mounting that would drop
  // the draft for nothing (the query reports the error).
  const [reloads, setReloads] = useState(0)
  const { refetch } = loaded
  const reload = useCallback(async (): Promise<void> => {
    const fetched = await refetch()
    if (fetched.isSuccess && fetched.data?.result.ok) setReloads((n) => n + 1)
  }, [refetch])

  let content: React.ReactNode
  // An open spec stays open when a later fetch of it fails: replacing it
  // with the error would drop the draft.
  if (!usable) {
    content = (
      <EmptyState
        message={reasonText(null)}
        subtitle="Go back to the project and open another tab spec."
      />
    )
  } else if (data?.result.ok) {
    content = (
      <TabSpecRoot
        key={reloads}
        projectId={project.id}
        owner={project.Created_by}
        name={name}
        document={data.result.document}
        etag={data.etag}
        dataOnly={dataOnly}
        onReload={reload}
      />
    )
  } else if (loaded.isPending) {
    content = (
      <div className="flex items-center justify-center h-full">
        <Loader fontSize={48} />
      </div>
    )
  } else if (loaded.isError) {
    content = (
      <EmptyState
        message="The tab spec could not be loaded."
        subtitle="Go back to the project and try again."
      />
    )
  } else {
    content = (
      <EmptyState
        message={reasonText(data)}
        subtitle="Go back to the project and open another tab spec."
      />
    )
  }

  // The assistant's panel docks on the right of the page, beside the spec.
  return (
    <AssistantDock projectId={project.id} owner={project.Created_by}>
      <div className="h-full p-3">{content}</div>
    </AssistantDock>
  )
}

export default TabSpecContainer
